import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trophy, Circle, Flame, Search, Crown, Shield, Medal, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronLeft, ChevronRight, MessagesSquare, Swords } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Loader, Empty, Pagination } from "@/components/Page";
import { CLASS_META, LIFECYCLE } from "@/lib/game";
import { Sigil, AllianceSigil } from "@/lib/cosmetics";
import { RetiredContent } from "@/pages/Retired";
import { useAuth } from "@/context/AuthContext";
import GoldenCrownIcon from "@/components/GoldenCrownIcon";

// Custom Rankings Icon
const RankingsIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/rankings-icon.png" 
    alt="Rankings" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

function CrownMarker({ gladiator, size = 14 }) {
  const crownName = gladiator.crown_name || (gladiator.bronze_crown ? "Bronze Crown" : gladiator.silver_crown ? "Silver Crown" : gladiator.golden_crown ? "Golden Crown" : null);
  return crownName ? <GoldenCrownIcon size={size} crownName={crownName} className="ml-1.5 animate-pulse" /> : null;
}

function GladiatorOfWeek() {
  const [g, setG] = useState(undefined); // undefined=loading, null=none
  useEffect(() => { api.get("/gladiator-of-week").then((r) => setG(r.data.gladiator)).catch(() => setG(null)); }, []);
  if (g === undefined || g === null) return null;
  const meta = CLASS_META[g.cls] || {};
  return (
    <div data-testid="gladiator-of-week" className="mb-6 border border-amber-600/50 bg-gradient-to-r from-amber-950/40 to-zinc-950 px-5 py-4 flex items-center gap-4 flex-wrap">
      <Crown className="text-amber-400 shrink-0" size={30} />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.25em] text-amber-500/80 font-cinzel">Gladiator of the Week</p>
        <Link data-testid="gotw-name" to={`/gladiator/${g.id}`} className="font-cinzel text-2xl text-amber-200 hover:text-amber-400 transition-colors">{g.name}</Link>
        <p className="text-xs text-zinc-400">
          <span className={meta.color}>{g.cls}</span> · <span className="text-amber-300 tabular-nums">{g.rating}</span> rating · trained by{" "}
          <Link to={`/profile/${g.owner_id}`} className="text-zinc-300 hover:text-amber-300 underline">{g.owner_name}</Link>
        </p>
      </div>
      <div className="ml-auto text-right">
        <p className="font-cinzel text-3xl text-amber-300 tabular-nums leading-none">{g.wins_this_week}</p>
        <p className="text-[11px] uppercase tracking-widest text-zinc-500">wins this week</p>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = ["retired", "alliances", "lanistae", "leaderboard", "crown"].includes(tabParam) ? tabParam : "rankings";
  const setTab = (t) => setSearchParams(t === "rankings" ? {} : { tab: t });
  
  const rankingsSubTab = searchParams.get("view");
  const rankingsView = ["elo", "brackets", "class"].includes(rankingsSubTab) ? rankingsSubTab : "rankings";
  const setRankingsView = (v) => {
    const params = tab === "rankings" ? {} : { tab };
    if (v !== "rankings") params.view = v;
    setSearchParams(params);
  };
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [minR, setMinR] = useState("");
  const [maxR, setMaxR] = useState("");
  const bracket = searchParams.get("bracket") || "beginner_bronze";
  const eloDivision = searchParams.get("division") || "bronze";

  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      params.set("page", page);
      if (q.trim()) params.set("q", q.trim());
      if (minR !== "") params.set("min_rating", minR);
      if (maxR !== "") params.set("max_rating", maxR);
      if (rankingsView === "brackets") params.set("bracket", bracket);
      if (rankingsView === "elo") params.set("division", eloDivision);
      setRows(null);
      api.get(`/leaderboard?${params.toString()}`, { signal: controller.signal }).then((r) => {
        if (controller.signal.aborted) return;
        setRows(r.data.leaderboard);
        setTotalPages(r.data.total_pages || 1);
        setTotal(r.data.total || 0);
      }).catch(() => {
        if (!controller.signal.aborted) setRows([]);
      });
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [page, q, minR, maxR, bracket, eloDivision, rankingsView]);

  const onFilter = (setter) => (v) => { setter(v); setPage(1); };
  const setRankingBracket = (next) => { setPage(1); setSearchParams({ view: "brackets", bracket: next }); };
  const setEloDivision = (next) => { setPage(1); setSearchParams({ view: "elo", division: next }); };

  return (
    <div>
      <PageHeader title="Arena Rankings" subtitle="Every gladiator in the arena, living and legendary" icon={RankingsIcon} />

      <div className="flex gap-2 mb-6 border-b border-zinc-800 flex-wrap">
        {[["rankings", "Live Rankings"], ["lanistae", "Lanista Rankings"], ["leaderboard", "Leaderboards"], ["alliances", "Alliances"], ["crown", "The Golden Crown"], ["retired", "Hall of the Retired"]].map(([id, label]) => (
          <button key={id} data-testid={`rankings-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              tab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "retired" ? <RetiredContent /> : tab === "crown" ? <CrownLeaderboard /> : tab === "alliances" ? <AllianceRankings /> : tab === "lanistae" ? <LanistaRankings /> : tab === "leaderboard" ? <StatLeaderboard /> : (
        // Live Rankings with sub-tabs
        <div>
          {/* Sub-tabs for Live Rankings */}
          <div className="flex gap-2 mb-6 border-b border-zinc-800">
            {[["rankings", "Rankings"], ["elo", "Division Rankings"], ["brackets", "Stat Rankings"], ["class", "Top 10 by Class"]].map(([id, label]) => (
              <button key={id} data-testid={`rankings-view-${id}`} onClick={() => setRankingsView(id)}
                className={`px-3 py-2 text-xs font-medium uppercase tracking-wide border-b-2 -mb-px transition-colors ${
                  rankingsView === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
                {label}
              </button>
            ))}
          </div>

          {rankingsView === "skill" ? <SkillRankings /> : rankingsView === "class" ? <ClassRankings /> : (
        <>
      <GladiatorOfWeek />
      {rankingsView === "brackets" && <div data-testid="ranking-bracket-tabs" className="mb-5 flex flex-wrap gap-2">
        {[["beginner_bronze", "Beginner–Bronze", "0–499"], ["silver_gold", "Silver–Gold", "500–1,499"], ["elite", "Elite", "1,500–2,499"], ["legendary", "Legendary", "2,500+"]].map(([key, label, range]) => (
          <button key={key} data-testid={`ranking-bracket-${key}`} onClick={() => setRankingBracket(key)}
            className={`border px-3 py-2 text-xs font-cinzel uppercase tracking-wide transition-colors ${bracket === key ? "border-amber-500 bg-amber-500 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-amber-700 hover:text-amber-300"}`}>
            {label} <span className="ml-1 text-[10px] opacity-75">{range}</span>
          </button>
        ))}
      </div>}
      {rankingsView === "elo" && <div data-testid="elo-division-tabs" className="mb-5 flex flex-wrap gap-2">
        {[["bronze", "Bronze", "0–1300"], ["silver", "Silver", "1301–1999"], ["golden", "Gold", "2000+"]].map(([key, label, range]) => (
          <button key={key} data-testid={`elo-division-${key}`} onClick={() => setEloDivision(key)} className={`border px-3 py-2 text-xs font-cinzel uppercase tracking-wide transition-colors ${eloDivision === key ? "border-amber-500 bg-amber-500 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-amber-700 hover:text-amber-300"}`}>{label} <span className="ml-1 text-[10px] opacity-75">{range}</span></button>
        ))}
      </div>}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="rankings-search" value={q} onChange={(e) => onFilter(setQ)(e.target.value)}
            placeholder="Search gladiator or lanista…"
            className="w-full bg-zinc-950 border border-amber-900/40 pl-9 pr-3 py-2.5 text-sm text-zinc-100 focus:border-amber-600 outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="rankings-min-rating" type="number" value={minR} onChange={(e) => onFilter(setMinR)(e.target.value)}
            placeholder="Min" className="w-24 bg-zinc-950 border border-amber-900/40 px-3 py-2.5 text-sm text-zinc-100 focus:border-amber-600 outline-none tabular-nums" />
          <span className="text-zinc-600 text-sm">–</span>
          <input data-testid="rankings-max-rating" type="number" value={maxR} onChange={(e) => onFilter(setMaxR)(e.target.value)}
            placeholder="Max" className="w-24 bg-zinc-950 border border-amber-900/40 px-3 py-2.5 text-sm text-zinc-100 focus:border-amber-600 outline-none tabular-nums" />
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 hidden sm:inline">rating</span>
        </div>
      </div>

      {rows === null ? <Loader /> : (
        <>
          {total > 0 && (
            <p className="text-[11px] text-zinc-500 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5"><Circle size={9} className="text-emerald-500 fill-emerald-500" /> Lanista online now</span>
              <span className="flex items-center gap-1.5"><Circle size={9} className="text-red-500 fill-red-500" /> Resting — already used their fight (cannot be challenged yet)</span>
            </p>
          )}
          {total === 0 ? <Empty label={q || minR || maxR ? "No gladiators match your filters." : "No ranked gladiators yet."} /> : (
            <>
              <div className="border border-amber-900/30 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                      <th className="text-left px-4 py-3">#</th>
                      <th className="text-left px-4 py-3">Gladiator</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Class</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Lanista</th>
                      <th className="text-right px-4 py-3">Rating</th>
                      <th className="text-right px-4 py-3 hidden sm:table-cell">W/L</th>
                      <th className="text-right px-4 py-3 hidden md:table-cell">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const meta = CLASS_META[r.cls] || {};
                      return (
                        <tr key={r.id} data-testid={`leader-row-${r.id}`} className={`border-b border-zinc-900 ${
                          r.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                        }`}>
                          <td className="px-4 py-3 font-cinzel text-amber-400">{r.rank}</td>
                          <td className="px-4 py-3 text-zinc-100">
                            <span className="inline-flex items-center gap-1.5">
                              {r.owner_online && (
                                <Circle data-testid={`online-dot-${r.id}`} size={9} className="text-emerald-500 fill-emerald-500 shrink-0"
                                  title="This gladiator's lanista is online now" />
                              )}
                              {r.fight_ready === false && (
                                <Circle data-testid={`resting-dot-${r.id}`} size={9} className="text-red-500 fill-red-500 shrink-0"
                                  title="Has already used their fight — resting" />
                              )}
                              <Link data-testid={`leader-name-${r.id}`} to={`/gladiator/${r.id}`}
                                className="text-zinc-100 hover:text-amber-400 transition-colors">
                                {r.alliance_tag && <span data-testid={`alliance-tag-${r.id}`} className="text-sky-400 font-cinzel mr-1">[{r.alliance_tag}]</span>}
                                {r.name}
                              </Link>
                              <CrownMarker gladiator={r} />
                              {r.streak >= 2 && (
                                <span data-testid={`streak-${r.id}`} title={`${r.streak}-win streak`}
                                  className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-orange-300 tabular-nums">
                                  <Flame size={11} className="text-orange-400 fill-orange-500/40" />{r.streak}
                                </span>
                              )}
                              {r.is_mine && <span className="ml-1 text-[10px] text-amber-500 uppercase">you</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell"><span className={meta.color}>{r.cls}</span></td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="inline-flex items-center gap-1.5">
                              {r.owner_sigil && <Sigil id={r.owner_sigil} size={16} />}
                              <Link className="text-zinc-400 hover:text-amber-300 underline" to={`/profile/${r.owner_id}`}>{r.owner_name}</Link>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-amber-300 tabular-nums">{r.rating}</td>
                          <td className="px-4 py-3 text-right text-zinc-400 hidden sm:table-cell">{r.wins}/{r.losses}</td>
                          <td className="px-4 py-3 text-right hidden md:table-cell">
                            <span className={`text-xs ${(LIFECYCLE[r.lifecycle] || {}).cls?.includes("red") ? "text-red-400" : "text-zinc-400"}`}>{r.age}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm">
                  <span className="text-[11px] uppercase tracking-widest text-zinc-500">{total} gladiators</span>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} testid="rankings" />
                </div>
              )}
            </>
          )}
        </>
      )}
      </>
      )}
      </div>
    )}
    </div>
  );
}

const MEDAL = { 1: "text-amber-400", 2: "text-zinc-300", 3: "text-amber-700" };

export function TrophyCase({ trophies, size = 14 }) {
  if (!trophies || trophies.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1" data-testid="trophy-case">
      {trophies.slice(0, 5).map((t) => (
        <span key={t.season} title={`#${t.rank} — ${t.season}`} className="inline-flex items-center">
          <Trophy size={size} className={MEDAL[t.rank] || "text-zinc-500"} />
        </span>
      ))}
    </span>
  );
}

function AllianceRankings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/alliances/leaderboard").then((r) => setData(r.data)).catch(() => setData({ leaderboard: [] })); }, []);

  if (data === null) return <Loader label="Tallying the season…" />;
  const rows = data.leaderboard || [];
  const seasonLabel = data.season
    ? new Date(`${data.season}-01T00:00:00`).toLocaleDateString([], { month: "long", year: "numeric" })
    : "";

  return (
    <div data-testid="alliance-rankings">
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Shield size={18} className="text-amber-400" />
          <h2 className="font-cinzel text-amber-200 text-lg">Alliance Season</h2>
          <span className="text-xs uppercase tracking-widest text-zinc-500">· {seasonLabel} · resets monthly</span>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">Score = quality of ranked wins (tougher opponents &amp; upsets are worth more). Only each alliance members top 5 contributing gladiators count toward the season score. The top alliance earns a trophy at month's end.</p>
      </div>
      {rows.length === 0 ? <Empty label="No alliances have fought this season yet." /> : (
        <div className="border border-amber-900/30 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Alliance</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Leader</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Members</th>
                <th className="text-right px-4 py-3">Season Score</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Trophies</th>
                {user?.is_admin && <th className="text-center px-4 py-3">Admin</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`alliance-leader-row-${r.id}`} className={`border-b border-zinc-900 ${
                  r.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                }`}>
                  <td className="px-4 py-3 font-cinzel">
                    {r.rank <= 3 ? <Medal size={18} className={MEDAL[r.rank]} /> : <span className="text-amber-400">{r.rank}</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-100">
                    <span className="inline-flex items-center gap-2">
                      {r.sigil ? <AllianceSigil id={r.sigil} size={22} /> : <Shield size={16} className="text-zinc-600" />}
                      <Link data-testid={`alliance-leader-name-${r.id}`} to={`/alliance/${r.id}`}
                        className="text-zinc-100 hover:text-amber-400 transition-colors">{r.name}</Link>
                      {r.tag && <span className="text-zinc-600 text-xs">[{r.tag}]</span>}
                      {r.is_mine && <span className="ml-1 text-[10px] text-amber-500 uppercase">yours</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{r.leader_name}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 hidden sm:table-cell tabular-nums">{r.member_count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-amber-300 tabular-nums">{r.season_score}</td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell"><span className="inline-flex justify-end w-full"><TrophyCase trophies={r.trophies} /></span></td>
                  {user?.is_admin && (
                    <td className="px-4 py-3 text-center">
                      <Link 
                        to={`/alliance/${r.id}/warroom`}
                        className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                        title="View War Room (admin)"
                      >
                        <MessagesSquare size={14} />
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TREND = {
  up: { icon: TrendingUp, cls: "text-emerald-400", title: "Rising — higher win rate this week than last" },
  down: { icon: TrendingDown, cls: "text-red-400", title: "Falling — lower win rate this week than last" },
  steady: { icon: Minus, cls: "text-zinc-600", title: "Steady — win rate holding (needs fights in both weeks to trend)" },
};

function LanistaRow({ r }) {
  const [open, setOpen] = useState(false);
  const Tr = TREND[r.trend] || TREND.steady;
  const comps = [
    ["Win Quality", r.components.win_quality],
    ["Win Rate", r.components.win_rate],
    ["Opponent Strength", r.components.opp_strength],
    ["Decisiveness", r.components.decisiveness],
    ["Fight Volume", r.components.volume],
    ["Elite roster bonus", `${r.elite_roster_bonus?.points || 0} (${r.elite_roster_bonus?.top10 || 0} Top 10 · ${r.elite_roster_bonus?.top100 || 0} Top 100)`],
  ];
  return (
    <>
      <tr data-testid={`lanista-row-${r.owner_id}`} onClick={() => setOpen((o) => !o)}
        className={`border-b border-zinc-900 cursor-pointer ${
          r.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
        }`}>
        <td className="px-4 py-3 font-cinzel">
          {r.rank <= 3 ? <Medal size={18} className={MEDAL[r.rank]} /> : <span className="text-amber-400">{r.rank}</span>}
        </td>
        <td className="px-4 py-3 text-zinc-100">
          <span className="inline-flex items-center gap-2">
            {open ? <ChevronDown size={14} className="text-zinc-500 shrink-0" /> : <ChevronRight size={14} className="text-zinc-500 shrink-0" />}
            {r.owner_online && <Circle size={9} className="text-emerald-500 fill-emerald-500 shrink-0" title="Online now" />}
            {r.owner_sigil && <Sigil id={r.owner_sigil} size={18} />}
            <Link data-testid={`lanista-name-${r.owner_id}`} to={`/profile/${r.owner_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-zinc-100 hover:text-amber-400 transition-colors">
              {r.alliance_tag && <span className="text-sky-400 font-cinzel mr-1">[{r.alliance_tag}]</span>}
              {r.owner_name}
            </Link>
            {r.is_mine && <span className="ml-1 text-[10px] text-amber-500 uppercase">you</span>}
            {r.provisional && <span className="ml-1 text-[10px] text-zinc-500 uppercase tracking-wide" title={`Provisional — needs more fights to rank at full strength`}>prov</span>}
          </span>
        </td>
        <td className="px-4 py-3 text-center"><Tr.icon size={16} className={`inline ${Tr.cls}`} title={Tr.title} /></td>
        <td className="px-4 py-3 text-right text-zinc-400 hidden sm:table-cell tabular-nums">{r.win_rate}%</td>
        <td className="px-4 py-3 text-right text-zinc-400 hidden md:table-cell tabular-nums">{r.avg_opp_rating}</td>
        <td className="px-4 py-3 text-right text-zinc-400 hidden sm:table-cell tabular-nums">{r.fights}</td>
        <td className="px-4 py-3 text-right font-semibold text-amber-300 tabular-nums">{r.score}</td>
      </tr>
      {open && (
        <tr data-testid={`lanista-breakdown-${r.owner_id}`} className="bg-zinc-950/80 border-b border-zinc-900">
          <td colSpan={7} className="px-4 py-4">
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="text-[11px] text-zinc-500 uppercase tracking-widest">
                Record <span className="text-zinc-300 normal-case tracking-normal text-sm">{r.wins}W / {r.losses}L</span>
                <span className="mx-2">·</span>Decisive wins <span className="text-zinc-300 text-sm">{r.decisive_pct}%</span>
                <span className="mx-2">·</span>Confidence <span className="text-zinc-300 text-sm">{Math.round(r.confidence * 100)}%</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {comps.map(([label, val]) => (
                <div key={label} className="border border-amber-900/30 bg-black/30 rounded-sm p-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
                  </div>
                  <div className="font-cinzel text-amber-300 text-xl tabular-nums mt-1">+{val}</div>
                </div>
              ))}
            </div>
            {r.provisional && <p className="text-[11px] text-zinc-500 mt-3">Provisional — this lanista needs more ranked fights in the window before their score reaches full strength.</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function LanistaRankings() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setData(null);
    api.get(`/leaderboard/lanistae?page=${page}`).then((r) => setData(r.data)).catch(() => setData({ leaderboard: [] }));
  }, [page]);

  if (data === null) return <Loader label="Weighing the lanistae…" />;
  const rows = data.leaderboard || [];
  const totalPages = data.total_pages || 1;

  return (
    <div data-testid="lanista-rankings">
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Crown size={18} className="text-amber-400" />
          <h2 className="font-cinzel text-amber-200 text-lg">Lanista Rankings</h2>
          <span className="text-xs uppercase tracking-widest text-zinc-500">· last {data.window_days || 14} days · updates live</span>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">
          Combined performance of all your gladiators. Tap on any lanista to see a points breakdown.
        </p>
      </div>
      {data.me && data.me.rank > 0 && (
        <div data-testid="lanista-my-rank" className="mb-4 flex items-center gap-3 border border-amber-700/40 bg-amber-950/20 px-4 py-3 rounded-sm">
          <span className="font-cinzel text-2xl text-amber-300 tabular-nums">#{data.me.rank}</span>
          <span className="text-sm text-zinc-300">Your rank — <span className="text-amber-300 font-semibold">{data.me.score}</span> pts · {data.me.wins}W/{data.me.losses}L over {data.me.fights} fights</span>
        </div>
      )}
      {rows.length === 0 ? <Empty label="No ranked fights in the last 14 days yet. Send some challenges to climb the board!" /> : (
        <>
          <div className="border border-amber-900/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Lanista</th>
                  <th className="text-center px-4 py-3" title="7-day momentum">Trend</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Win %</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">Avg Opp</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Fights</th>
                  <th className="text-right px-4 py-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => <LanistaRow key={r.owner_id} r={r} />)}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4" data-testid="lanista-pagination">
              <button data-testid="lanista-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                <ChevronLeft size={15} /> Prev
              </button>
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums">
                Page {page} of {totalPages} · {data.total} lanistae
              </span>
              <button data-testid="lanista-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                Next <ChevronRight size={15} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


const STAT_METRICS = [["wins", "Most Wins"], ["fights", "Most Fights"], ["decisive", "Most Decisive Wins"], ["losses", "Most Losses"], ["tournament_wins", "Most Tournament Wins"]];
const GLADIATOR_ONLY_STAT_METRICS = [["best_streak", "Highest Win Streak"]];
const STAT_PERIODS = [["week", "This Week"], ["month", "This Month"], ["all", "All Time"]];

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(([id, label]) => (
        <button key={id} data-testid={`${testid}-${id}`} onClick={() => onChange(id)}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
            value === id ? "bg-amber-500 text-zinc-950 border-amber-500 font-semibold"
                         : "bg-transparent text-zinc-400 border-zinc-800 hover:border-amber-700/50 hover:text-amber-300"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function StatLeaderboard() {
  const [entity, setEntity] = useState("gladiator");
  const [metric, setMetric] = useState("wins");
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/leaderboard/stats?entity=${entity}&metric=${metric}&period=${period}`)
      .then((r) => setData(r.data)).catch(() => setData({ leaderboard: [] }));
  }, [entity, metric, period]);

  const metricOptions = entity === "gladiator" ? [...STAT_METRICS, ...GLADIATOR_ONLY_STAT_METRICS] : STAT_METRICS;
  const setLeaderboardEntity = (nextEntity) => {
    setEntity(nextEntity);
    if (nextEntity === "lanista" && metric === "best_streak") setMetric("wins");
  };
  const cols = metric === "tournament_wins" 
    ? [["tournament_wins", "Tourney W"]]
    : metric === "best_streak"
      ? [["best_streak", "Best Streak"]]
    : [["wins", "W"], ["fights", "F"], ["decisive", "Dec"], ["losses", "L"]];
  const rows = data?.leaderboard || [];

  return (
    <div data-testid="stat-leaderboard">
      <div className="space-y-3 mb-5">
        <Pills options={[["gladiator", "Gladiators"], ["lanista", "Lanistae"]]} value={entity} onChange={setLeaderboardEntity} testid="stat-entity" />
        <Pills options={metricOptions} value={metric} onChange={setMetric} testid="stat-metric" />
        {metric !== "best_streak" && <Pills options={STAT_PERIODS} value={period} onChange={setPeriod} testid="stat-period" />}
      </div>

      {data === null ? <Loader label="Tallying the records…" /> : rows.length === 0 ? (
        <Empty label={metric === "tournament_wins" ? "No tournament victories yet." : "No fights recorded for this timeframe yet."} />
      ) : (
        <div className="border border-amber-900/30 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">{entity === "gladiator" ? "Gladiator" : "Lanista"}</th>
                {cols.map(([key, label]) => (
                  <th key={key} className={`text-right px-4 py-3${metric === key ? " text-amber-300" : ""}`}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`stat-row-${r.id}`}
                  className={`border-b border-zinc-900 ${
                    r.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                  }`}>
                  <td className="px-4 py-3 font-cinzel">
                    {r.rank <= 3 ? <Medal size={18} className={MEDAL[r.rank]} /> : <span className="text-amber-400">{r.rank}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      {entity === "lanista" && r.online && <Circle size={9} className="text-emerald-500 fill-emerald-500 shrink-0" title="Online now" />}
                      {entity === "lanista" && r.sigil && <Sigil id={r.sigil} size={18} />}
                      <Link data-testid={`stat-name-${r.id}`}
                        to={entity === "gladiator" ? `/gladiator/${r.id}` : `/profile/${r.id}`}
                        className="text-zinc-100 hover:text-amber-400 transition-colors text-left">
                        {r.alliance_tag && <span className="text-sky-400 font-cinzel mr-1">[{r.alliance_tag}]</span>}
                        {r.name}
                      </Link>
                      {r.retired && <span className="text-[10px] text-zinc-500 uppercase">retired</span>}
                      {r.is_mine && <span className="text-[10px] text-amber-500 uppercase">you</span>}
                      {entity === "gladiator" && r.sub && <span className="text-[11px] text-zinc-500">· {r.sub}</span>}
                    </span>
                  </td>
                  {cols.map(([key]) => (
                    <td key={key} className={`px-4 py-3 text-right tabular-nums ${metric === key ? "text-amber-300 font-semibold" : "text-zinc-400"}`}>{r[key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CrownLeaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [page, setPage] = useState(1);
  const [crownType, setCrownType] = useState("golden");
  const historyMode = crownType === "golden" ? "wins" : "youngest";

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.get(`/leaderboard/crown?crown_type=${crownType}&sort=${historyMode}&page=${page}&limit=10`)
      .then((r) => {
        if (!active) return;
        setData(r.data);
      })
      .catch(() => {
        if (active) setError("The Crown records could not be refreshed.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, crownType, historyMode, retryKey]);

  const hasRequestedData = data?.crown_type === crownType;
  if (!hasRequestedData && loading) return <Loader />;
  if (!hasRequestedData && error) return <div data-testid="crown-leaderboard-error" className="border border-red-900/60 bg-red-950/20 px-4 py-5 text-center"><p className="text-sm text-red-200">{error}</p><button data-testid="crown-leaderboard-retry" onClick={() => setRetryKey((key) => key + 1)} className="mt-3 border border-red-700 px-3 py-1.5 text-xs uppercase tracking-widest text-red-200 transition-colors hover:bg-red-900/30">Retry</button></div>;
  if (!hasRequestedData) return <Loader />;

  const totalPages = Math.max(1, Math.ceil(data.total / (data.per_page || 10)));

  return (
    <div className="space-y-6" data-testid="crown-leaderboard">
      <div data-testid="crown-history-tabs" className="flex flex-wrap gap-2">
        {["bronze", "silver", "golden"].map((type) => {
          const label = `${type[0].toUpperCase()}${type.slice(1)} Crown`;
          return <button key={type} data-testid={`crown-history-tab-${type}`} onClick={() => { setPage(1); setCrownType(type); }} className={`border px-3 py-2 text-xs font-cinzel uppercase tracking-wide transition-colors ${crownType === type ? "border-amber-500 bg-amber-500 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-amber-700 hover:text-amber-300"}`}>{label}</button>;
        })}
      </div>
      {error && <div data-testid="crown-leaderboard-refresh-error" className="flex flex-wrap items-center justify-between gap-2 border border-red-900/50 bg-red-950/15 px-3 py-2 text-xs text-red-200"><span>{error} Showing the last confirmed records.</span><button data-testid="crown-leaderboard-refresh-retry" onClick={() => setRetryKey((key) => key + 1)} className="uppercase tracking-widest text-red-100 underline underline-offset-2">Retry</button></div>}
      {/* Golden Crown: Single-holder hero card */}
      {crownType === "golden" && (
        data.current_holder ? (
          <div className="border border-amber-500 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-6 rounded-sm text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <GoldenCrownIcon size={120} crownName={data.crown_name} className="animate-pulse filter blur-[1px]" />
            </div>
            <div className="inline-flex items-center gap-2 mb-2 animate-bounce">
              <GoldenCrownIcon size={32} crownName={data.crown_name} />
            </div>
            <h2 className="font-cinzel text-xl sm:text-3xl text-amber-300 uppercase tracking-wide">The {data.crown_name} Champion</h2>
            <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">Reigns Supreme in the Arena</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-y border-amber-900/30 py-4 max-w-xl mx-auto">
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Gladiator</span>
                <Link to={`/gladiator/${data.current_holder.id}`}
                  className="font-cinzel text-lg sm:text-2xl text-zinc-100 hover:text-amber-400 underline decoration-dotted underline-offset-4">
                  {data.current_holder.name}
                </Link>
              </div>
              <div className="h-8 border-r border-amber-900/20 hidden sm:block" />
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Lanista</span>
                <Link to={`/profile/${data.current_holder.owner_id}`}
                  className="font-cinzel text-lg sm:text-2xl text-zinc-400 hover:text-amber-300 underline decoration-dotted underline-offset-4">
                  {data.current_holder_owner}
                </Link>
              </div>
              <div className="h-8 border-r border-amber-900/20 hidden sm:block" />
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Wins & Defenses</span>
                <span className="font-cinzel text-lg sm:text-2xl text-amber-300 tabular-nums">{data.current_holder.crown_wins}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 rounded-sm font-cinzel uppercase">
            The {data?.crown_name || "Crown"} currently lies in the dust.
          </div>
        )
      )}

      {/* Bronze / Silver: Three-slot hero header */}
      {crownType !== "golden" && (
        <div className="border border-amber-500 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-6 rounded-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <GoldenCrownIcon size={120} crownName={data.crown_name} className="filter blur-[1px]" />
          </div>
          <div className="inline-flex items-center gap-2 mb-1">
            <GoldenCrownIcon size={28} crownName={data.crown_name} />
          </div>
          <h2 className="font-cinzel text-xl sm:text-3xl text-amber-300 uppercase tracking-wide">The {data.crown_name} Champions</h2>
          <p className="text-xs text-zinc-500 mt-1 mb-5 uppercase tracking-widest">Current Division Holders</p>
          <div data-testid="crown-slot-holders" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((slot) => {
              const slotKey = `${crownType}_${slot}`;
              const slotLabel = ["I", "II", "III"][slot - 1];
              const holder = data.current_holders?.find((h) => h.crown_slot === slotKey);
              return (
                <div key={slotKey} className="border border-amber-900/40 bg-zinc-950/60 p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-400 mb-2">
                    <GoldenCrownIcon size={12} crownName={data.crown_name} />
                    {data.crown_name} {slotLabel}
                  </div>
                  {holder ? (
                    <>
                      <Link to={`/gladiator/${holder.id}`}
                        className="font-cinzel text-base text-zinc-100 hover:text-amber-400 underline decoration-dotted underline-offset-4 block">
                        {holder.name}
                      </Link>
                      <div className="text-xs text-zinc-500 mt-1">
                        <Link to={`/profile/${holder.owner_id}`} className="hover:text-amber-300 transition-colors">
                          {holder.owner_name}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <span className="font-cinzel text-base text-zinc-600 italic">Vacant</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Crown History Leaderboard list */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3"><h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">{data.crown_type === "golden" ? "Golden Crown Wins & Defenses" : `${data.crown_name} Youngest Winners`}</h3></div>
        {data.leaderboard.length === 0 ? (
          <Empty label="No gladiator has held the crown yet." />
        ) : (
          <>
            <div className="border border-amber-900/30 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                    <th className="text-left px-4 py-3">Gladiator</th>
                    <th className="text-left px-4 py-3">Lanista</th>
                    <th className="text-right px-4 py-3">{data.history_mode === "youngest" ? "Winning Age" : "Total Crown Wins & Defenses"}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leaderboard.map((r) => (
                    <tr 
                      key={r.glad_id} 
                      className={`border-b border-zinc-900 ${
                        user?.user_id && r.owner_id === user.user_id ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                      }`}
                    >
                      <td className="px-4 py-3 text-zinc-100">
                        <span className="inline-flex items-center gap-1.5">
                          <Link to={`/gladiator/${r.glad_id}`}
                            className="font-cinzel hover:text-amber-400 text-zinc-200 text-left transition-colors">
                            {r.glad_name}
                          </Link>
                          {r.is_current_holder && (
                            <GoldenCrownIcon size={14} crownName={data.crown_name} className="animate-pulse" />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        <Link to={`/profile/${r.owner_id}`}
                          className="hover:text-amber-300 underline text-left transition-colors">
                          {r.owner_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right font-cinzel text-amber-300 tabular-nums font-semibold text-base">{data.history_mode === "youngest" ? r.age : r.crown_wins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500">{data.total} champions</span>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} testid="crown-rankings" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function ClassRankings() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    setData(null);
    api.get("/class-rankings").then((r) => setData(r.data)).catch(() => setData({ classes: {} }));
  }, []);

  if (!data) return <Loader />;

  const classOrder = ["dimachaerus", "retiarius", "provocator"];
  const classes = classOrder.map(key => data.classes[key]).filter(Boolean);

  return (
    <div className="space-y-8">
      {classes.map((classData) => (
        <div key={classData.name} data-testid={`class-ranking-${classData.name.toLowerCase()}`}>
          <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
            <Swords className="text-amber-500" size={20} />
            {classData.name}
          </h2>

          {classData.gladiators.length === 0 ? (
            <Empty label={`No ${classData.name} gladiators found.`} />
          ) : (
            <div className="border border-amber-900/30 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Gladiator</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Lanista</th>
                    <th className="text-right px-4 py-3">Rating</th>
                    <th className="text-right px-4 py-3 hidden sm:table-cell">W/L</th>
                    <th className="text-right px-4 py-3 hidden md:table-cell">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {classData.gladiators.map((g, idx) => {
                    const meta = CLASS_META[g.cls] || {};
                    return (
                      <tr key={g.id} data-testid={`class-ranking-row-${g.id}`} className={`border-b border-zinc-900 ${
                        g.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                      }`}>
                        <td className="px-4 py-3 font-cinzel text-amber-400">{idx + 1}</td>
                        <td className="px-4 py-3 text-zinc-100">
                          <span className="inline-flex items-center gap-1.5">
                            {g.owner_online && (
                              <Circle size={9} className="text-emerald-500 fill-emerald-500 shrink-0" title="Lanista online" />
                            )}
                            {g.fight_ready === false && (
                              <Circle size={9} className="text-red-500 fill-red-500 shrink-0" title="Resting" />
                            )}
                            <Link to={`/gladiator/${g.id}`} className="text-zinc-100 hover:text-amber-400 transition-colors">
                              {g.alliance_tag && <span className="text-sky-400 font-cinzel mr-1">[{g.alliance_tag}]</span>}
                              {g.name}
                            </Link>
                            <CrownMarker gladiator={g} />
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Link to={`/profile/${g.owner_id}`} className="text-zinc-400 hover:text-amber-400 transition-colors">
                            {g.owner_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-amber-300">{g.rating}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-400 hidden sm:table-cell">
                          {g.wins}–{g.losses}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-500 hidden md:table-cell">
                          {(20 + g.age_weeks / 4.3).toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function SkillRankings() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    setData(null);
    api.get("/skill-rankings").then((r) => setData(r.data)).catch(() => setData({ tiers: {} }));
  }, []);

  if (!data) return <Loader />;

  const tierOrder = ["beginner_bronze", "silver_gold", "elite", "legendary"];
  const tiers = tierOrder.map(key => data.tiers[key]).filter(Boolean);

  return (
    <div className="space-y-8">
      {tiers.map((tier) => (
        <div key={tier.name} data-testid={`skill-tier-${tier.name.toLowerCase()}`}>
          <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
            <Medal className="text-amber-500" size={20} />
            {tier.name}
            <span className="text-sm text-zinc-500 font-normal">({tier.min}–{tier.max > 9000 ? tier.max === 10**9 ? "∞" : "2500+" : tier.max} total skill)</span>
          </h2>

          {tier.gladiators.length === 0 ? (
            <Empty label={`No gladiators in the ${tier.name} bracket yet.`} />
          ) : (
            <div className="border border-amber-900/30 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Gladiator</th>
                    <th className="text-left px-4 py-3 hidden sm:table-cell">Class</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Lanista</th>
                    <th className="text-right px-4 py-3">Rating</th>
                    <th className="text-right px-4 py-3 hidden sm:table-cell">W/L</th>
                    <th className="text-right px-4 py-3 hidden md:table-cell">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {tier.gladiators.map((g, idx) => {
                    const meta = CLASS_META[g.cls] || {};
                    return (
                      <tr key={g.id} data-testid={`skill-tier-row-${g.id}`} className={`border-b border-zinc-900 ${
                        g.is_mine ? "ring-2 ring-orange-500 bg-orange-950/20" : "hover:bg-zinc-900/40"
                      }`}>
                        <td className="px-4 py-3 font-cinzel text-amber-400">{idx + 1}</td>
                        <td className="px-4 py-3 text-zinc-100">
                          <span className="inline-flex items-center gap-1.5">
                            {g.owner_online && (
                              <Circle size={9} className="text-emerald-500 fill-emerald-500 shrink-0" title="Lanista online" />
                            )}
                            {g.fight_ready === false && (
                              <Circle size={9} className="text-red-500 fill-red-500 shrink-0" title="Resting" />
                            )}
                            <Link to={`/gladiator/${g.id}`} className="text-zinc-100 hover:text-amber-400 transition-colors">
                              {g.alliance_tag && <span className="text-sky-400 font-cinzel mr-1">[{g.alliance_tag}]</span>}
                              {g.name}
                            </Link>
                            {g.golden_crown && (
                              <GoldenCrownIcon size={14} className="ml-1.5 animate-pulse" />
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={meta.color}>{g.cls}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Link to={`/profile/${g.owner_id}`} className="text-zinc-400 hover:text-amber-400 transition-colors">
                            {g.owner_name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-amber-300">{g.rating}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-400 hidden sm:table-cell">
                          {g.wins}–{g.losses}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-500 hidden md:table-cell">
                          {(20 + g.age_weeks / 4.3).toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
